import React, {useEffect, useState} from "react";
import axios from "axios";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {baseUrl, userLocation} from "../Univ.js";
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";

// Local-time YYYY-MM-DD (toISOString shifts the date by a day in India).
const iso = (d) =>
    d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";

const money = (v) =>
    "₹ " + Number(v || 0).toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2});

const startOfMonth = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
};

function PharmProfit() {
    const [startDate, setStartDate] = useState(startOfMonth());
    const [endDate, setEndDate] = useState(new Date());
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // ---------------- FETCH (one request) ----------------
    useEffect(() => {
        if (!startDate || !endDate) return;
        let cancelled = false;

        (async () => {
            setIsLoading(true);
            setError("");
            try {
                const res = await axios.get(`${baseUrl}pharm-profit-statement/`, {
                    params: {start_date: iso(startDate), end_date: iso(endDate), location: userLocation || 1},
                });
                if (!cancelled) setData(res.data);
            } catch (err) {
                console.log(err);
                if (!cancelled) {
                    setData(null);
                    setError(
                        err?.response?.status === 404
                            ? "Profit API not found (404). Add the pharm-profit-statement/ route in the Django urls.py."
                            : "Unable to load the profit statement. Check the Django terminal for the error."
                    );
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [startDate, endDate]);

    // ---------------- PRESETS ----------------
    const setRange = (from, to) => {
        setStartDate(from);
        setEndDate(to);
    };
    const today = new Date();
    const presets = [
        {label: "Today", run: () => setRange(today, today)},
        {label: "This Month", run: () => setRange(startOfMonth(), today)},
        {
            label: "Last Month",
            run: () => setRange(
                new Date(today.getFullYear(), today.getMonth() - 1, 1),
                new Date(today.getFullYear(), today.getMonth(), 0)
            ),
        },
        {label: "This Year", run: () => setRange(new Date(today.getFullYear(), 0, 1), today)},
    ];

    const s = data?.summary;

    // ---------------- PROFIT STATEMENT ROWS ----------------
    const statement = s
        ? [
            {label: "Gross sales (before discount)", value: s.gross_sales},
            {label: "Less: Discount given", value: -s.discount},
            {label: "Net sales", value: s.net_sales, bold: true},
            {label: "Less: Cost of goods sold (purchase cost of items sold)", value: -s.cogs},
            {label: "Gross profit", value: s.gross_profit, bold: true, profit: true},
            {label: "Estimated GST included in sales", value: -s.gst_in_sales_estimate, note: true},
            {label: "Profit excluding GST (estimate)", value: s.profit_ex_gst_estimate, note: true},
        ]
        : [];

    const otherInfo = s
        ? [
            ["Bills", s.bills],
            ["Sales returns (not deducted above)", money(s.sales_returns)],
            ["Purchases made in this period (GRN)", money(s.purchases_in_period)],
            ["Purchase returns", money(s.purchase_returns)],
            ["Amount collected", money(s.collected)],
            ["Outstanding / credit balance", money(s.outstanding)],
        ]
        : [];

    // ---------------- GRID ----------------
    const cell = (p) => money(p.value);
    const columns = [
        {field: "product", headerName: "Product", flex: 1, minWidth: 240},
        {field: "qty", headerName: "Qty Sold", type: "number", width: 110},
        {field: "sales", headerName: "Sales", type: "number", width: 130, renderCell: cell},
        {field: "cost", headerName: "Cost", type: "number", width: 130, renderCell: cell},
        {field: "profit", headerName: "Profit", type: "number", width: 130, renderCell: cell},
        {
            field: "margin_percent",
            headerName: "Margin %",
            type: "number",
            width: 110,
            renderCell: (p) => `${Number(p.value || 0).toFixed(2)}%`,
        },
    ];
    const gridRows = (data?.by_product || []).map((r, i) => ({id: i, ...r}));

    // ---------------- RENDER ----------------
    return (
        <section className="container mt-4">

            <h3 className="text-center bg-info text-white p-2">Pharmacy Profit Statement</h3>

            {/* ---------------- FILTER ---------------- */}
            <div className="row mb-3 align-items-center">
                <div className="col-md-3 mb-2">
                    <DatePicker
                        selected={startDate}
                        onChange={setStartDate}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        dateFormat="dd-MM-yyyy"
                        className="form-control"
                        placeholderText="From Date"
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <DatePicker
                        selected={endDate}
                        onChange={setEndDate}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        dateFormat="dd-MM-yyyy"
                        className="form-control"
                        placeholderText="To Date"
                    />
                </div>
                <div className="col-md-6 mb-2 d-flex flex-wrap gap-2">
                    {presets.map((p) => (
                        <button key={p.label} type="button" className="btn btn-outline-info btn-sm" onClick={p.run}>
                            {p.label}
                        </button>
                    ))}
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => window.print()}>
                        Print
                    </button>
                </div>
            </div>

            {isLoading && <div className="text-center my-3">Calculating profit...</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            {s && !isLoading && (
                <>
                    {/* data-quality warnings */}
                    {(s.bills_skipped > 0 || s.items_without_cost > 0) && (
                        <div className="alert alert-warning">
                            {s.bills_skipped > 0 && <div>{s.bills_skipped} bill(s) skipped (product/qty/amount lists did not match).</div>}
                            {s.items_without_cost > 0 && <div>{s.items_without_cost} sold item(s) had no cost record, so profit may be overstated.</div>}
                        </div>
                    )}

                    {/* ---------------- KPI CARDS ---------------- */}
                    <div className="row text-center mb-3">
                        {[
                            ["Net Sales", money(s.net_sales)],
                            ["Cost of Goods Sold", money(s.cogs)],
                            ["Gross Profit", money(s.gross_profit)],
                            ["Margin % (on sales)", `${s.margin_percent}%`],
                            ["Markup % (on cost)", `${s.markup_percent}%`],
                        ].map(([title, value]) => (
                            <div className="col-md-2 col-6 mb-2 flex-fill" key={title}>
                                <div className="card p-2 bg-light h-100">
                                    <h6>{title}</h6>
                                    <h5>{value}</h5>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row">
                        {/* ---------------- STATEMENT ---------------- */}
                        <div className="col-lg-7 mb-3">
                            <table className="erp-table-modern">
                                <thead>
                                <tr className="table-info">
                                    <th>Profit Statement ({data.from} to {data.to})</th>
                                    <th className="text-end" style={{width: 180}}>Amount</th>
                                </tr>
                                </thead>
                                <tbody>
                                {statement.map((r) => (
                                    <tr key={r.label} className={r.profit ? "table-success" : ""}>
                                        <td className={r.note ? "text-muted" : ""}
                                            style={{fontWeight: r.bold ? "bold" : "normal"}}>{r.label}</td>
                                        <td className={`text-end ${r.value < 0 ? "text-danger" : ""}`}
                                            style={{fontWeight: r.bold ? "bold" : "normal"}}>{money(r.value)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* ---------------- OTHER INFO ---------------- */}
                        <div className="col-lg-5 mb-3">
                            <table className="erp-table-modern">
                                <thead>
                                <tr className="table-light"><th colSpan={2}>Other figures</th></tr>
                                </thead>
                                <tbody>
                                {otherInfo.map(([k, v]) => (
                                    <tr key={k}><td>{k}</td><td className="text-end">{v}</td></tr>
                                ))}
                                {data.by_mode.map((m) => (
                                    <tr key={m.mode}><td>Sales by {m.mode}</td><td className="text-end">{money(m.amount)}</td></tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ---------------- DAILY CHART ---------------- */}
                    <h6 className="mt-2">Day-wise sales, cost and profit</h6>
                    <div style={{width: "100%", height: 300}}>
                        <ResponsiveContainer>
                            <BarChart data={data.by_day}>
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis dataKey="date"/>
                                <YAxis/>
                                <Tooltip formatter={(v) => money(v)}/>
                                <Legend/>
                                <Bar dataKey="sales" name="Sales" fill="#0dcaf0"/>
                                <Bar dataKey="cost" name="Cost" fill="#adb5bd"/>
                                <Bar dataKey="profit" name="Profit" fill="#198754"/>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* ---------------- PRODUCT-WISE ---------------- */}
                    <h6 className="mt-4">Product-wise profit (before bill-level discount)</h6>
                    <DataGrid
                        sx={{
                            boxShadow: 4,
                            border: 4,
                            borderColor: "primary.light",
                            "& .MuiDataGrid-cell:hover": {color: "primary.main"},
                        }}
                        rows={gridRows}
                        columns={columns}
                        autoHeight
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                            pagination: {paginationModel: {pageSize: 25}},
                            sorting: {sortModel: [{field: "profit", sort: "desc"}]},
                        }}
                        slots={{toolbar: GridToolbar}}
                    />
                </>
            )}

            {!isLoading && !error && data && data.by_day.length === 0 && (
                <div className="alert alert-secondary text-center">No pharmacy bills in this period.</div>
            )}
        </section>
    );
}

export default PharmProfit;
